import { useEffect, useState } from 'react'
import SmsActivityPanel from './SmsActivityPanel'
import { fetchSmsLog } from '../../services/smsLogService'

/**
 * SMS Log — the standalone screen.
 *
 * The activity panel already existed, but only inside Admin, behind the
 * user-management permission. Everything the platform texts is written to
 * `sms_log`, and the people who need to check whether a seller was actually
 * contacted are not the people who administer users — so the same panel now
 * has its own place in the sidebar, and carries its own toast.
 *
 * The banner exists because the panel's own tiles cannot explain themselves.
 * A column reading `0 delivered / 42 no receipt` looks like forty-two failures,
 * when the truth is that nothing has failed and the carrier is simply not
 * telling us either way: delivery receipts only arrive if the provider is
 * configured to post them back. Saying so is the difference between a number
 * that worries someone and a number they can act on.
 */
export default function SmsLogView() {
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null)
  const [sent, setSent] = useState(0)
  const [receipts, setReceipts] = useState(0)

  const showToast = (msg: string, err?: boolean) => {
    setToast({ msg, err: !!err })
    window.setTimeout(() => setToast(null), 4000)
  }

  useEffect(() => {
    let live = true
    fetchSmsLog('all', 500)
      .then((res) => {
        if (!live) return
        const rows = res.rows || []
        setSent(rows.length)
        setReceipts(rows.filter((r) => r.delivery_status).length)
      })
      .catch(() => { /* the panel below reports its own load errors */ })
    return () => { live = false }
  }, [])

  const receiptsMissing = sent > 0 && receipts === 0

  return (
    <div className="sms-log-view">
      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 9999,
            maxWidth: 420,
            padding: '12px 16px',
            borderRadius: 8,
            color: '#fff',
            background: toast.err ? '#b3261e' : '#1f6f3f',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          }}
        >
          {toast.msg}
        </div>
      )}
      {receiptsMissing && (
        <div
          style={{
            margin: '0 0 16px',
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid rgba(240, 180, 41, 0.30)',
            background: 'rgba(240, 180, 41, 0.10)',
            color: '#f0d9a8',
            lineHeight: 1.5,
          }}
        >
          <b>No delivery receipts are arriving.</b> Every message below left the
          platform and the carrier accepted it — but none of them has come back
          with a delivered or undelivered verdict, so “no receipt” here means
          unknown, not failed. Receipts start appearing once the provider is
          pointed at <code>/api/sms/webhook</code> with a matching
          <code> TALLBOB_WEBHOOK_TOKEN</code> set on the Pages project.
        </div>
      )}
      <SmsActivityPanel showToast={showToast} />
    </div>
  )
}
